import * as React from "react";
import * as TreeRepository from '../../../Repository/TreeRepository';
import CarouselItem from "./CarouselItem";
import TreeDescription from "./TreeDescription";
import message from "antd/es/message";
import { Carousel, Empty, Modal, Pagination, Space, Row, Col } from "antd";
import { useEffect, useRef, useState } from "react";
import { Contract, Tree, User } from "tree-wallet-client";
import { UserRoles } from "../../../Repository/UserRepository";
import { isAllowed } from "../../../Service/AuthService";
import MapPopupErrorBoundary from "./MapPopupErrorBoundary";
import MapPopupCertifyButton from "./MapPopupCertifyButton";
import MapPopupDeleteButton from "./MapPopupDeleteButton";
import { collapseModalAtPoint } from "../../../Helper/PopupCollapse";
import { Spin } from "antd/es";
import PhotoViewer from "../../../Component/PhotoViewer";

interface MapPopupProps {
  language: string;
  contracts?: Contract[];
  reloadTrees: Function;
  updateState: Function;
  user: User;
  treeId: number;
  onClose?: () => void;
  onCloseStart?: () => void;
  // Where this tree's icon sits on screen: the closing animation is directed at it.
  getMarkerScreenPoint?: () => { x: number; y: number } | null;
}

export interface ValidatedTree {
  id: string;
  type: string;
  number: string;
  validationData?: any;
}

export default function MapPopup(props: MapPopupProps) {
  const { treeId, language, reloadTrees, user, updateState, contracts, onClose } = props;

  const [isValidated, setIsValidated] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(true);

  // Set when the window is closing right after its validation was written to the queue: it then
  // collapses towards the queue button the tree landed in, instead of towards the tree's icon.
  const queuedValidationRef = useRef(false);

  // Screen centre of the validator's queue button. Looked up in the DOM because the queue lives in
  // its own component and all that is needed here is a position - the same approach as the planter
  // form's `getQueueScreenPoint`.
  const getQueueScreenPoint = () => {
    const button = document.querySelector('.btn-open-validated-queue');
    if (!button) {
      return null;
    }

    const rect = button.getBoundingClientRect();
    // The queue is not shown at every screen size: a button with no box has no position to aim at.
    if (!rect.width || !rect.height) {
      return null;
    }

    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  };

  const initiateClose = () => {
    props.onCloseStart?.();
    // The window collapses into its own tree icon instead of into the middle of the screen - or, when
    // the validation has just been saved, towards the queue button it was written to.
    const collapsePoint = (queuedValidationRef.current ? getQueueScreenPoint() : null)
      ?? props.getMarkerScreenPoint?.()
      ?? null;
    collapseModalAtPoint(collapseAnchorRef.current, collapsePoint);
    setOpen(false);
  };
  const [tree, setTree] = useState<Tree | undefined>(undefined);
  const [contract, setContract] = useState<Contract | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false); // For delete button
  const [pageLoading, setPageLoading] = useState<boolean>(true); // For initial load
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);
  const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);
  const [validationInProgress, setValidationInProgress] = useState<boolean>(false); // Track validation state
  // Photo shown by the full screen viewer; empty means the viewer is closed.
  const [viewerImageUrl, setViewerImageUrl] = useState<string | undefined>(undefined);
  // When delete confirmation popup is open, Validate button is disabled
  // (the delete button reports it, see `MapPopupDeleteButton`)
  const [deleteBusy, setDeleteBusy] = useState<boolean>(false);
  const carouselRef = useRef<any>(null);
  // Any node inside the window is enough for the closing collapse, see `collapseModalAtPoint`.
  const collapseAnchorRef = useRef<HTMLSpanElement>(null);

  // Fetch tree data
  const fetchTreeData = async () => {
    setPageLoading(true);
    setPreviewImage(undefined); // Clear any existing preview when switching trees
    try {
      const treeData = await TreeRepository.getTree({ params: { treeId } });
      setTree(treeData);
      if (treeData) {
        const contractData = contracts?.find((c) => Number(c.id) === Number(treeData.contractId));
        setContract(contractData);
        setIsValidated(treeData.validationHash !== null);

        // Default to the newest photo (last position due to oldest-first sorting)
        const photoCount = treeData.photos?.length || 0;
        if (photoCount > 0) {
          // If there's a preview image, newest photo is at index photoCount, otherwise photoCount - 1
          const defaultIndex = previewImage ? photoCount : photoCount - 1;
          setCurrentPhotoIndex(defaultIndex);
        }
      }
    } catch (err) {
      console.error(err);
      message.error('Tree not found');
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    fetchTreeData();
  }, [treeId, contracts]);

  const isAdmin = isAllowed(user.roles, [UserRoles.Admin]);
  // const isValidator = isAllowed(contract?.roles, [ContractRepository.roles.Validator]);

  // Sort photos by creation date (oldest first, newest last)
  const sortedPhotos = tree?.photos
    ? [...tree.photos].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    : [];

  const onPageChange = (page: number) => {
    setCurrentPhotoIndex(page - 1);
    carouselRef.current?.goTo(page - 1);
  };

  const handlePhotoCapture = (url?: string) => {
    setPreviewImage(url);
    if (url) {
      setCurrentPhotoIndex(0);
      carouselRef.current?.goTo(0);
    }
  };

  const openViewer = (url: string) => {
    setViewerImageUrl(url);
  };

  const totalItems = (sortedPhotos?.length || 0) + (previewImage ? 1 : 0);

  return (
    <>
      <Modal
        visible={open}
        maskClosable={false}
        title={''}
        width={600}
        onCancel={initiateClose}
        onOk={initiateClose}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        okButtonProps={{ disabled: validationInProgress }}
        bodyStyle={{ touchAction: 'auto' }}
        // The popup is torn down (its root unmounted by `TreeMaps`) once the close animation has
        // played. It used to be torn down by an effect as soon as `open` became false, which
        // removed the modal from the DOM before `ant-zoom-leave` could start: the window vanished
        // instead of collapsing - and there was nothing left to direct the collapse at.
        afterClose={onClose}
      >
        {/* Any node inside the window is enough for the collapse (`collapseModalAtPoint` looks the
            panel up from it). Hidden, so it takes no room in the modal body. */}
        <span ref={collapseAnchorRef} style={{ display: 'none' }} />
        <MapPopupErrorBoundary>
          {pageLoading
            ? (
              <div>
                <div>
                  Loading...
                </div>
                <div>
                  <Empty description={
                    <span>
                      Loading data ... <Spin />
                    </span>
                  } image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              </div>
            )
            : tree ? (
              <>
                <div>
                  {(totalItems > 1)
                    ? <Pagination
                      onChange={onPageChange}
                      current={currentPhotoIndex + 1}
                      total={totalItems}
                      pageSize={1}
                      size="small"
                    />
                    : null
                  }
                </div>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div style={{ maxWidth: '550px', width: '100%', margin: '0 auto' }}>
                    {totalItems === 0 && <Empty description={'No image'} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    {totalItems === 1 && (
                      <div className="map-tree description">
                        {previewImage && (
                          <CarouselItem
                            key="preview"
                            imageUrl={previewImage}
                            isActive={true}
                            onClick={openViewer}
                          />
                        )}
                        {!previewImage && sortedPhotos && sortedPhotos.length > 0 && (
                          <CarouselItem
                            key={sortedPhotos[0].id}
                            imageUrl={sortedPhotos[0]?.imageUrl}
                            isActive={true}
                            onClick={openViewer}
                          />
                        )}
                      </div>
                    )}
                    {totalItems > 1 && (
                      <Carousel
                        ref={carouselRef}
                        afterChange={(index) => {
                          setCurrentPhotoIndex(index);
                        }}
                        initialSlide={currentPhotoIndex}
                        arrows={true}
                        dots={false}
                        swipeToSlide={true}
                        draggable={true}
                        touchMove={true}
                        infinite={false}
                        speed={300}
                        easing="ease-out"
                        style={{ touchAction: 'auto' }}
                      >
                        {previewImage && (
                          <CarouselItem
                            key="preview"
                            imageUrl={previewImage}
                            isActive={0 === currentPhotoIndex}
                            onClick={openViewer}
                          />
                        )}
                        {sortedPhotos && sortedPhotos.map((p, index) => {
                          return (
                            <CarouselItem
                              key={p.id}
                              imageUrl={p?.imageUrl}
                              isActive={index === currentPhotoIndex}
                              onClick={openViewer}
                            />
                          );
                        })}
                      </Carousel>
                    )}
                  </div>
                  <div>
                    <TreeDescription
                      tree={tree}
                      user={user}
                      language={language}
                      isValidated={isValidated}
                      contract={contract}
                    />
                  </div>
                  <div>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {contract && (
                        <Row justify="start" align="middle" gutter={16}>
                          <Col>
                            <MapPopupDeleteButton
                              tree={tree}
                              user={user}
                              isAdmin={isAdmin}
                              isValidated={isValidated}
                              loading={loading}
                              contract={contract}
                              setLoading={setLoading}
                              setOpenedPopup={initiateClose}
                              reloadTrees={reloadTrees}
                              validationInProgress={validationInProgress}
                              onBusyChange={setDeleteBusy}
                            />
                          </Col>
                          <Col>
                            <MapPopupCertifyButton
                              setIsValidated={setIsValidated}
                              isAdmin={isAdmin}
                              language={language}
                              tree={tree}
                              user={user}
                              contract={contract}
                              updateState={updateState}
                              deleteBusy={deleteBusy}
                              onPhotoCapture={handlePhotoCapture}
                              setValidationInProgress={setValidationInProgress}
                              onValidationSuccess={() => {
                                // The validation really is in the queue by now (the form only reports
                                // success once the write has answered): it is what the window
                                // collapses towards as it closes.
                                queuedValidationRef.current = true;
                                fetchTreeData();
                                initiateClose();
                              }}
                            />
                          </Col>
                        </Row>
                      )}
                    </Space>
                  </div>
                </Space>
              </>
            ) : (
              <Empty description={'Tree data could not be loaded.'} />
            )
          }
        </MapPopupErrorBoundary>
      </Modal>

      <PhotoViewer url={viewerImageUrl} onClose={() => setViewerImageUrl(undefined)} />
    </>
  );
}

